import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { Bank, Branch, Country } from '@/interfaces/beneficiar';
import { Currency, SwiftCurrency } from '@/interfaces/currency';
import dataServices from '@/api/dataServices';
import { ExchangeRate } from '@/interfaces/exchangeRate';
import moment from 'moment';
import { CheckbookType } from '@/interfaces/checkbook';
import { getReferenceCurrency, getInternalBankCode } from '@/utils/currencyConfig';

interface EXC {
  buyingRate: number | { parsedValue: number; source?: string };
  effectiveDate: string;
  sellingRate: number;
  sourceCurrency: string;
  targetCurrency: string;
}

const getNumericRate = (rate: unknown): number | undefined => {
  if (typeof rate === 'number' && Number.isFinite(rate)) {
    return rate;
  }

  if (typeof rate === 'object' && rate !== null && 'parsedValue' in rate) {
    const parsedValue = (rate as { parsedValue?: unknown }).parsedValue;
    return typeof parsedValue === 'number' && Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

interface CurrencyMap {
  [key: string]: CurrencyObj;
}

interface CurrencyObj {
  [key: string]: number;
}

interface DataState {
  currency: Currency[];
  swiftCurrencies: SwiftCurrency[];
  banks: Bank[];
  countries: Country[];
  branches: Branch[];
  availableBenCodes: any[];
  checkbookTypes: CheckbookType[];
  periodicities: any[];
  loading: boolean;
  error: string;
  exchangeRates: ExchangeRate | undefined;
  lastUpdate: string;
}

const initialState: DataState = {
  currency: [],
  swiftCurrencies: [],
  banks: [],
  availableBenCodes: [],
  countries: [],
  checkbookTypes: [],
  branches: [],
  periodicities: [],
  loading: false,
  exchangeRates: undefined,
  error: '',
  lastUpdate: '',
};

export const fetchDetails = createAsyncThunk('data/details', async () => {
  return dataServices.getDetails();
});

export const fetchExchangeRates = createAsyncThunk('data/exchangeRates', async () => {
  return dataServices.getExchRates();
});

function normalizeRates(response: EXC[]) {
  const rates: CurrencyMap = {};
  const referenceCurrency = getReferenceCurrency();

  response.forEach(rate => {
    const buyingRate = getNumericRate(rate.buyingRate);
    if (buyingRate === undefined) return;

    rates[rate.sourceCurrency] = rates[rate.sourceCurrency] || {};
    rates[rate.sourceCurrency][rate.targetCurrency] = buyingRate;
  });

  Object.keys(rates).forEach(sourceCurrency => {
    if (sourceCurrency !== referenceCurrency) {
      Object.keys(rates[sourceCurrency]).forEach(targetCurrency => {
        const sourceRate = rates[sourceCurrency][targetCurrency];
        if (sourceRate <= 0) return;

        rates[targetCurrency] = rates[targetCurrency] || {};
        if (rates[targetCurrency][sourceCurrency] === undefined) {
          rates[targetCurrency][sourceCurrency] = 1 / sourceRate;
        }
      });
    }
  });

  Object.keys(rates).forEach(sourceCurrency => {
    if (sourceCurrency !== referenceCurrency) {
      Object.keys(rates[sourceCurrency]).forEach(intermediateCurrency => {
        Object.keys(rates[intermediateCurrency]).forEach(targetCurrency => {
          if (sourceCurrency !== targetCurrency && rates[sourceCurrency][targetCurrency] === undefined) {
            const rate1 = rates[sourceCurrency][intermediateCurrency];
            const rate2 = rates[intermediateCurrency][targetCurrency];
            rates[sourceCurrency][targetCurrency] = +(rate1 * rate2).toFixed(4);
          } else if (sourceCurrency === targetCurrency) {
            rates[sourceCurrency][targetCurrency] = 1;
          }
        });
      });
    }
  });

  return rates;
}

const normalizeCurrencies = (currencies: any) => {
  return currencies.map((curr: any) => ({ ...curr, name: curr.alphabeticCode }));
};

const normalizeCheckbookTypes = (checkbookTypes: any) => {
  return checkbookTypes.map((checkbookType: any) => ({
    id: checkbookType.id,
    code: checkbookType.code,
    name: checkbookType.label,
    currency: checkbookType.currency,
    numberOfLeaves: checkbookType.numberOfLeaves,
    fee: checkbookType.fee ?? null,
  }));
};

const normalizeBanks = (banks: any) => {
  return banks.filter((bank: any) => bank.bankCode !== getInternalBankCode());
};

const normalizeSwift = (swiftCurrencies: any) => {
  return swiftCurrencies.map((curr: any) => ({ code: curr.code, name: curr.code }));
};

export const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    updateBeneficiaryAccountNumbers: (state, action) => {
      switch (action.payload.action) {
        case 'add': {
          const isAlreadyAdded = state.availableBenCodes.some((acc: any) => acc.accountNumber === action.payload.accountNumber);
          if (!isAlreadyAdded) {
            const updatedAccountNumbers = [
              ...state.availableBenCodes,
              { accountNumber: action.payload.accountNumber, name: action.payload.name },
            ];
            state.availableBenCodes = updatedAccountNumbers;
          }
          break;
        }
        case 'update': {
          const updatedAccountNumbers = state.availableBenCodes.map((acc: any) =>
            acc.accountNumber === action.payload.accountNumber || acc.name === action.payload.name
              ? { ...acc, accountNumber: action.payload.accountNumber, name: action.payload.name }
              : acc
          );
          state.availableBenCodes = updatedAccountNumbers;
          break;
        }
        case 'delete': {
          const updatedAccountNumbers = state.availableBenCodes.filter((acc: any) => acc.accountNumber !== action.payload.accountNumber);
          state.availableBenCodes = updatedAccountNumbers;
          break;
        }
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchDetails.pending, state => {
      state.loading = true;
    });
    builder.addCase(fetchDetails.fulfilled, (state, action) => {
      const { payload } = action;

      state.currency = normalizeCurrencies(payload.currencies);
      state.banks = normalizeBanks(payload.banks);
      state.countries = payload.countries;
      state.availableBenCodes = payload.beneficiaries;
      state.checkbookTypes = normalizeCheckbookTypes(payload.checkbookTypes);
      state.swiftCurrencies = normalizeSwift(payload.swiftCurrencies);
      state.branches = payload.branches;
      state.periodicities = payload.periodicities || [];
      state.loading = false;
    });
    builder.addCase(fetchDetails.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(fetchExchangeRates.pending, state => {
      state.loading = true;
    });

    builder.addCase(fetchExchangeRates.fulfilled, (state, action) => {
      const { payload } = action;
      state.exchangeRates = normalizeRates(payload.exchangeRates);
      state.loading = false;
      state.lastUpdate = moment().utc().format('MMM DD, YYYY, HH:mm [UTC]');
    });

    builder.addCase(fetchExchangeRates.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });
  },
});

export const selectData = (state: RootState) => state.data;

export const { updateBeneficiaryAccountNumbers } = dataSlice.actions;

export default dataSlice.reducer;
